import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import {
  DAILY_DEPARTMENTS,
  DAILY_PRIORITIES,
  type DailyDepartment,
  type DailyTaskStatus,
} from './constants.js';

/** Zod hatasını alan yollu sorunlara çevirir. */
export function dailyIssues(error: z.ZodError, prefix: string[] = []): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: [...prefix, ...issue.path.map(String)].join('.'),
    code: issue.code === 'invalid_value' ? 'INVALID' : issue.message,
  }));
}

const day = z.string().regex(/^\d{4}-\d{2}-(0[1-9]|[12]\d|3[01])$/, 'INVALID_DATE');
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'INVALID_TIME');
const text = (max: number) =>
  z
    .string()
    .max(max, 'TOO_LONG')
    .transform((v) => v.trim());
const optionalText = (max: number) =>
  z
    .string()
    .max(max, 'TOO_LONG')
    .nullish()
    .transform((v) => {
      const trimmed = v?.trim() ?? '';
      return trimmed === '' ? null : trimmed;
    });

const taskSchema = z.strictObject({
  planDay: day,
  startTime: time,
  endTime: time.nullish().transform((v) => v ?? null),
  department: z.enum(DAILY_DEPARTMENTS),
  description: text(500).refine((v) => v.length > 0, 'REQUIRED'),
  responsible: text(120).refine((v) => v.length > 0, 'REQUIRED'),
  priority: z.enum(DAILY_PRIORITIES),
  workOrderId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  permitRequired: z.boolean().default(false),
  riskNote: optionalText(1000),
  sparesTools: optionalText(1000),
});

export type DailyTaskInput = z.output<typeof taskSchema>;

/**
 * Görev alanları (ADR 0051 §4): gün ve saat biçimi, liste değerleri, bitiş başlangıçtan önce
 * olamaz (`INVALID_TIME_RANGE`).
 */
export function parseDailyTask(input: unknown): ParseResult<DailyTaskInput> {
  const parsed = taskSchema.safeParse(input);
  if (!parsed.success) return { ok: false, issues: dailyIssues(parsed.error) };
  const value = parsed.data;
  if (value.endTime !== null && value.endTime < value.startTime) {
    return { ok: false, issues: [{ path: 'endTime', code: 'INVALID_TIME_RANGE' }] };
  }
  return { ok: true, value };
}

/** İzin verilen durum geçişleri (ADR 0051 §4); geri dönüş yok. */
const NEXT: Readonly<Record<DailyTaskStatus, readonly DailyTaskStatus[]>> = {
  planned: ['in_progress', 'completed', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

export function isTaskClosed(status: DailyTaskStatus): boolean {
  return status === 'completed' || status === 'cancelled';
}

export interface TaskTransition {
  from: DailyTaskStatus;
  to: DailyTaskStatus;
  /** Görevin gemi yerel günü. */
  planDay: string;
  /** Şimdinin gemi yerel günü (gemi sunucusu saati). */
  today: string;
  completionNote: string | null;
  cancelReason: string | null;
}

/**
 * Durum geçişinin kuralları (ADR 0051 §4): geri dönüş yok (`INVALID_TRANSITION`); tamamlamada not
 * zorunlu (`COMPLETION_NOTE_REQUIRED`) ve gelecek güne planlanmış görev tamamlanamaz
 * (`FUTURE_DATE`); iptal gerekçe ister (`REASON_REQUIRED`).
 */
export function assertTaskTransition(input: TaskTransition): void {
  if (input.from === input.to) return;
  if (!NEXT[input.from].includes(input.to)) {
    throw new DomainError('INVALID_TRANSITION', `${input.from} -> ${input.to}`);
  }
  if (input.to === 'completed') {
    if ((input.completionNote ?? '').trim() === '') {
      throw new DomainError('COMPLETION_NOTE_REQUIRED');
    }
    if (input.planDay > input.today) throw new DomainError('FUTURE_DATE', input.planDay);
  }
  if (input.to === 'cancelled' && (input.cancelReason ?? '').trim() === '') {
    throw new DomainError('REASON_REQUIRED');
  }
}

/** Şimdinin gemi yerel günü ve saati (`HH:MM`). */
export interface ShipClock {
  day: string;
  time: string;
}

export function shipClock(nowUtc: string, tzOffsetMin: number): ShipClock {
  const local = new Date(Date.parse(nowUtc) + tzOffsetMin * 60_000).toISOString();
  return { day: local.slice(0, 10), time: local.slice(11, 16) };
}

/**
 * Gecikmiş görev: tamamlanmamış, iptal edilmemiş ve bitiş anı (bitiş yoksa başlangıç) gemi saatine
 * göre geçmiş (ADR 0051 §4). Saklanmaz.
 */
export function isTaskOverdue(
  task: { status: DailyTaskStatus; planDay: string; startTime: string; endTime: string | null },
  clock: ShipClock,
): boolean {
  if (isTaskClosed(task.status)) return false;
  if (task.planDay !== clock.day) return task.planDay < clock.day;
  return (task.endTime ?? task.startTime) < clock.time;
}

export interface DailySummary {
  total: number;
  completed: number;
  inProgress: number;
  planned: number;
  overdue: number;
  cancelled: number;
  byDepartment: { department: DailyDepartment; done: number; total: number }[];
}

/**
 * "Today's Summary" (BGM): durum sayıları, gecikmiş ayrı; departmana göre biten/toplam. İptal
 * edilen görev toplamda sayılmaz. Gecikmiş görev "planned" ya da "in progress" sayısına girmez.
 */
export function dailySummary(
  tasks: readonly {
    status: DailyTaskStatus;
    department: DailyDepartment;
    planDay: string;
    startTime: string;
    endTime: string | null;
  }[],
  clock: ShipClock,
): DailySummary {
  const summary: DailySummary = {
    total: 0,
    completed: 0,
    inProgress: 0,
    planned: 0,
    overdue: 0,
    cancelled: 0,
    byDepartment: [],
  };
  const departments = new Map<DailyDepartment, { done: number; total: number }>();
  for (const task of tasks) {
    if (task.status === 'cancelled') {
      summary.cancelled += 1;
      continue;
    }
    summary.total += 1;
    const entry = departments.get(task.department) ?? { done: 0, total: 0 };
    entry.total += 1;
    if (task.status === 'completed') {
      summary.completed += 1;
      entry.done += 1;
    } else if (isTaskOverdue(task, clock)) summary.overdue += 1;
    else if (task.status === 'in_progress') summary.inProgress += 1;
    else summary.planned += 1;
    departments.set(task.department, entry);
  }
  summary.byDepartment = DAILY_DEPARTMENTS.filter((d) => departments.has(d)).map((department) => ({
    department,
    ...(departments.get(department) as { done: number; total: number }),
  }));
  return summary;
}
